import { Activity, GameFullResponse } from "../types/gameFull";
import { TagPlayerOverrides } from "./buildTagPayload";
import { getActivitiesForPA } from "./plateAppearanceFeed";

/**
 * Background re-tags that put the hitter and pitcher on every pitch's omnibus
 * tag (the CSV's Pitcher/Batter columns), sent one at a time by useTagQueue.
 * The queue holds pitch ids, not payloads: each is built when it's sent, from
 * the latest data, so a later change never sends stale state.
 */

export type TagQueueEntry =
  /** Untagged pitch of an ended at bat: a players-only tag */
  | { activityId: string; kind: "fill"; isFinal?: boolean }
  /** New hitter/pitcher for the pitch's at bat */
  | { activityId: string; kind: "players"; players: TagPlayerOverrides };

type QueuedTagAction = "retag" | "fill" | "skip";

const isTagged = (activity: Activity) => Boolean(activity.result?.pitchResult);

/**
 * Whether an untagged pitch can take a fill: its at bat ended and it was
 * thrown by then. Pitches after the ending pitch belong to the next batter
 * once the game service releases them, so they wait for that at bat.
 * `isFinal` (End Game) fills the open at bat's pitches too.
 */
const canFill = (
  data: GameFullResponse,
  activity: Activity,
  isFinal = false,
) => {
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === activity.plateAppearanceId,
  );
  if (!pa) return false;
  if (isFinal) return true;
  if (pa.status !== "ended" || !pa.activityIds.includes(activity.activityId)) {
    return false;
  }
  const endingPitch = getActivitiesForPA(data, pa.plateAppearanceId)
    .filter(isTagged)
    .at(-1);
  return (
    !endingPitch || activity.pitch.eventTime <= endingPitch.pitch.eventTime
  );
};

/** What to send for a queued pitch, decided when it's sent. */
export const resolveQueuedTag = (
  data: GameFullResponse,
  entry: TagQueueEntry,
): QueuedTagAction => {
  const activity = data.activities.find(
    (a) => a.activityId === entry.activityId,
  );
  if (!activity) return "skip";
  if (isTagged(activity)) return entry.kind === "players" ? "retag" : "skip";
  const isFinal = entry.kind === "fill" && entry.isFinal;
  return canFill(data, activity, isFinal) ? "fill" : "skip";
};

/**
 * Most tags queued for one at bat, by a fill or a player change. Only a game
 * left running (in test environments) gets past this, and tagging all of it
 * would send hundreds of tags. An at bat's latest pitches are queued; End
 * Game skips the open at bat entirely, rather than holding End Game up.
 */
export const MAX_TAGS_PER_PLATE_APPEARANCE = 30;

/**
 * Queues fills for an at bat's pitches that can take one, after the rest:
 * the latest MAX_TAGS_PER_PLATE_APPEARANCE of them. At End Game (`isFinal`),
 * none if there are more.
 */
export const enqueueFills = (
  queue: TagQueueEntry[],
  data: GameFullResponse,
  plateAppearanceId: string,
  { isFinal = false }: { isFinal?: boolean } = {},
): TagQueueEntry[] => {
  const queued = new Set(queue.map((entry) => entry.activityId));
  const fills = getActivitiesForPA(data, plateAppearanceId)
    .map(
      (a): TagQueueEntry => ({
        activityId: a.activityId,
        kind: "fill",
        ...(isFinal && { isFinal }),
      }),
    )
    .filter(
      (entry) =>
        !queued.has(entry.activityId) &&
        resolveQueuedTag(data, entry) !== "skip",
    );
  if (isFinal && fills.length > MAX_TAGS_PER_PLATE_APPEARANCE) return queue;
  return [...queue, ...fills.slice(-MAX_TAGS_PER_PLATE_APPEARANCE)];
};

/** Whether End Game will fill any of the open plate appearance's pitches. */
export const hasFinalFills = (
  data: GameFullResponse,
  plateAppearanceId: string | null | undefined,
) =>
  Boolean(plateAppearanceId) &&
  enqueueFills([], data, plateAppearanceId!, { isFinal: true }).length > 0;

/**
 * Queues an at bat's new players ahead of everything else, in pitch order:
 * the latest MAX_TAGS_PER_PLATE_APPEARANCE of its pitches. A pitch already
 * queued is replaced, keeping the players it was waiting on. Pitches that
 * need nothing (untagged on the open at bat, filled when it ends) and
 * `exclude`d ones aren't queued.
 */
export const enqueuePlayerChange = (
  queue: TagQueueEntry[],
  data: GameFullResponse,
  plateAppearanceId: string,
  players: TagPlayerOverrides,
  { exclude = [] }: { exclude?: string[] } = {},
): TagQueueEntry[] => {
  const previous = new Map(queue.map((entry) => [entry.activityId, entry]));
  const changes = getActivitiesForPA(data, plateAppearanceId)
    .filter((a) => !exclude.includes(a.activityId))
    .map((a): TagQueueEntry => {
      const queued = previous.get(a.activityId);
      return {
        activityId: a.activityId,
        kind: "players",
        players:
          queued?.kind === "players"
            ? { ...queued.players, ...players }
            : players,
      };
    })
    .filter((entry) => resolveQueuedTag(data, entry) !== "skip")
    .slice(-MAX_TAGS_PER_PLATE_APPEARANCE);
  const changed = new Set(changes.map((entry) => entry.activityId));
  return [
    ...changes,
    ...queue.filter((entry) => !changed.has(entry.activityId)),
  ];
};

/**
 * The next entry to send and the queue without it. Entries whose pitch is
 * blocked (its own tag is still saving) wait in place.
 */
export const takeNextEntry = (
  queue: TagQueueEntry[],
  isBlocked: (activityId: string) => boolean,
): [TagQueueEntry | undefined, TagQueueEntry[]] => {
  const index = queue.findIndex((entry) => !isBlocked(entry.activityId));
  if (index === -1) return [undefined, queue];
  return [queue[index], queue.filter((_, i) => i !== index)];
};

/** What a pitch row shows while its tag is being sent. */
export type PitchStatus =
  | "saving"
  | "fill"
  | "hitter"
  | "pitcher"
  | "hitterAndPitcher";

const toPitchStatus = (entry: TagQueueEntry): PitchStatus => {
  if (entry.kind === "fill") return "fill";
  const { hitter, pitcher } = entry.players;
  if (hitter && pitcher) return "hitterAndPitcher";
  return hitter ? "hitter" : "pitcher";
};

const PITCH_STATUS_LABELS: Record<PitchStatus, string | null> = {
  saving: null,
  fill: "Adding hitter & pitcher…",
  hitter: "Updating hitter…",
  pitcher: "Updating pitcher…",
  hitterAndPitcher: "Updating hitter & pitcher…",
};

/** Row text for a queued tag; null while the pitch's own tag saves. */
export const pitchStatusLabel = (status: PitchStatus) =>
  PITCH_STATUS_LABELS[status];

/**
 * Every row's status: queued tags, and the pitch being sent (until its
 * request returns), over the pitches whose tags are saving.
 */
export const toPitchStatuses = (
  savingIds: ReadonlySet<string>,
  queue: TagQueueEntry[],
  inFlight: TagQueueEntry | null,
): ReadonlyMap<string, PitchStatus> => {
  const statuses = new Map<string, PitchStatus>();
  savingIds.forEach((id) => statuses.set(id, "saving"));
  if (inFlight) statuses.set(inFlight.activityId, toPitchStatus(inFlight));
  // A queued entry is newer than the one in flight for the same pitch
  queue.forEach((entry) =>
    statuses.set(entry.activityId, toPitchStatus(entry)),
  );
  return statuses;
};
